import { Fragment, useEffect, useRef } from 'react';
import { IconChat, IconPhone } from '../icons.jsx';

/**
 * Both what was written and what was delivered are kept on every message, so a
 * translation mistake stays visible rather than invisible.
 *
 * The two directions are deliberately not symmetrical:
 *
 *   the visitor's own message   shown exactly as typed, never translated and
 *                               never rewritten
 *   the agent's reply           shown in the visitor's chosen language first,
 *                               with the English underneath in smaller text,
 *                               and any further chosen languages after it
 *
 * The English line is not "the agent's original" — it is whichever rendition is
 * actually English. When the agent writes English those are the same string;
 * when they do not, the server stored an English rendition alongside the rest
 * precisely so this line never quietly shows a third language instead.
 */

/** The renditions to show under an agent reply, in order, never duplicated. */
function secondaryLines(message, extraLanguages) {
  const primary = message.translated ?? message.content;
  const bag = message.translations ?? {};
  const lines = [];
  const seen = new Set([primary]);

  const push = (lang, text) => {
    if (!text || seen.has(text)) return;
    seen.add(text);
    lines.push({ lang, text });
  };

  // English first: it is the one line the visitor is promised under every
  // reply, whichever other languages they picked.
  push('en', message.sourceLang === 'en' ? message.content : bag.en);
  for (const lang of extraLanguages) {
    if (lang !== 'en') push(lang, bag[lang]);
  }
  return lines;
}

const clock = (at) =>
  new Date(at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

/**
 * The label on a day separator.
 *
 * "Today" and "Yesterday" rather than a date, because those are the two the
 * reader actually has to place; anything older is dated properly. Built from
 * the browser's own locale, so it follows the visitor rather than the desk.
 */
function dayLabel(at) {
  const d = new Date(at);
  if (Number.isNaN(d.getTime())) return '';

  const midnight = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round((midnight(new Date()) - midnight(d)) / 86_400_000);

  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return d.toLocaleDateString([], { weekday: 'long' });
  return d.toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' });
}

/** Whether two timestamps fall on different calendar days for this reader. */
function newDay(prev, at) {
  if (!prev) return true;
  const a = new Date(prev);
  const b = new Date(at);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return false;
  return a.toDateString() !== b.toDateString();
}

export function MessageList({ messages, typing, languages = [], languageNames = {} }) {
  const end = useRef(null);

  useEffect(() => {
    end.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages.length, typing]);

  if (messages.length === 0) {
    return (
      <div className="messages empty">
        <span className="empty-ic" aria-hidden>
          <IconChat size={24} />
        </span>
        <p className="empty-title">How can we help?</p>
        <p className="empty-text">
          Ask a question in your own language. We will reply in it.
        </p>
        <div ref={end} />
      </div>
    );
  }

  // Everything after the primary. The primary is the big line, so listing it
  // again underneath would only repeat itself.
  const extras = languages.slice(1);
  const label = (code) => languageNames[code] ?? code.toUpperCase();

  return (
    <div className="messages" role="log" aria-live="polite">
      {messages.map((m, i) => {
        const own = m.sender === 'visitor';
        const separator = newDay(messages[i - 1]?.at, m.at) && (
          <p className="day-sep">{dayLabel(m.at)}</p>
        );

        if (own) {
          // Exactly what they typed. No translation, no alteration.
          return (
            <Fragment key={m.id}>
              {separator}
              <div className="msg own">
                <div className={m.spoken ? 'bubble spoken' : 'bubble'}>
                  {m.spoken && (
                    <span className="spoken-tag">
                      <IconPhone size={10} />
                      spoken on call
                    </span>
                  )}
                  <p lang={m.sourceLang}>{m.content}</p>
                </div>
                <time dateTime={m.at}>{clock(m.at)}</time>
              </div>
            </Fragment>
          );
        }

        const primary = m.translated ?? m.content;
        const primaryLang = m.translated ? (m.targetLang ?? m.sourceLang) : m.sourceLang;
        const lines = secondaryLines(m, extras);
        // With one rendition underneath there is nothing to tell apart, so the
        // language tag is dropped and the quotes carry it. With several the tag
        // is the only thing that says which is which.
        const tagged = lines.length > 1;

        return (
          <Fragment key={m.id}>
            {separator}
            <div className="msg">
              <div className={m.spoken ? 'bubble spoken' : 'bubble'}>
                {m.spoken && (
                  <span className="spoken-tag">
                    <IconPhone size={10} />
                    spoken on call
                  </span>
                )}
                <p lang={primaryLang}>{primary}</p>

                {lines.map((line) => (
                  <p key={line.lang} className="msg-alt" lang={line.lang}>
                    {tagged && <span className="msg-alt-label">{label(line.lang)}</span>}
                    &ldquo;{line.text}&rdquo;
                  </p>
                ))}

                {m.translationFailed && (
                  <span className="untranslated" title="Translation was unavailable">
                    shown untranslated
                  </span>
                )}
              </div>
              <time dateTime={m.at}>{clock(m.at)}</time>
            </div>
          </Fragment>
        );
      })}

      {typing && (
        <div className="msg">
          <div className="bubble typing" aria-label={`${typing} is typing`}>
            <span className="typing-dots" aria-hidden>
              <i />
              <i />
              <i />
            </span>
            <span className="typing-who">{typing} is typing</span>
          </div>
        </div>
      )}

      <div ref={end} />
    </div>
  );
}
